import Service from '@ember/service';
import fetch from 'fetch';
import config from 'admin-panel/config/environment';

import type { KeenTimeframe } from 'admin-panel/utils/dashboard-timeframe';

export type KeenAnalysis = 'count' | 'sum' | 'average' | 'extraction';

export interface KeenFilter {
  property_name: string;
  operator: 'eq' | 'gte' | 'lte';
  property_value: boolean | number | string | Array<number | string>;
}

export interface KeenQuery {
  event_collection: string;
  filters: KeenFilter[];
  timeframe: KeenTimeframe;
  timezone: string;
  group_by?: string;
  interval?: 'hourly' | 'daily';
  property_names?: string[];
  target_property?: string;
}

export interface KeenIntervalRow {
  timeframe: { start: string; end: string };
  value: number | null;
}

export type KeenGroupedRow = Record<string, number | string | null>;

export default class KeenService extends Service {
  async query<Result>(
    analysis: KeenAnalysis,
    query: KeenQuery,
    { signal }: { signal?: AbortSignal } = {}
  ): Promise<Result> {
    const { host, projectId, readKey } = config.keen;

    if (!readKey) {
      throw new Error('Missing Keen read key.');
    }

    const response = await fetch(
      `${host}/3.0/projects/${projectId}/queries/${analysis}`,
      {
        method: 'POST',
        headers: {
          Authorization: readKey,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(query),
        signal,
      }
    );

    if (!response.ok) {
      const body = (await response.json().catch(() => undefined)) as
        | { message?: string }
        | undefined;

      throw new Error(
        body?.message || `Keen query failed with status ${response.status}.`
      );
    }

    return ((await response.json()) as { result: Result }).result;
  }
}

declare module '@ember/service' {
  interface Registry {
    keen: KeenService;
  }
}
